import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const NOTE_MAX_LENGTH = 140;

const fmt = (d) =>
  d
    ? d.toLocaleDateString("en-IN", {
        weekday: "short",
        day: "numeric",
        month: "long",
      })
    : "";

const nights = (a, b) => {
  if (!a || !b) return 0;
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
};

export default function BookingDialog({
  open,
  range,
  memberName,
  onClose,
  onConfirm,
}) {
  const [note, setNote] = useState("");
  const [blockType, setBlockType] = useState("hard");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setNote("");
      setBlockType("hard");
      setBusy(false);
    }
  }, [open]);

  const submit = async () => {
    setBusy(true);
    await onConfirm(note.trim(), blockType);
    setBusy(false);
  };

  const soft = blockType === "soft";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="sm:max-w-md bg-white border border-[#DCE0D9] rounded-none p-0"
        data-testid="booking-dialog"
      >
        <div className="p-8">
          <DialogHeader className="space-y-2">
            <p className="tracker text-[10px] text-[#9E473F]">Confirm the stay</p>
            <DialogTitle className="font-display text-3xl text-[#1A2F24] leading-tight">
              {memberName}, hold the house.
            </DialogTitle>
            <DialogDescription className="font-body text-sm text-[#5C6B61]">
              Once confirmed, the family gets an email &amp; WhatsApp and the dates lock.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6">
            <p className="tracker text-[10px] text-[#5C6B61]">How firm is the plan?</p>
            <div className="mt-2 grid grid-cols-2 gap-2" role="radiogroup" data-testid="block-type-toggle">
              {[
                { id: "hard", label: "Hard block", desc: "Confirmed. Dates are locked." },
                { id: "soft", label: "Soft block", desc: "Tentative. Reminders at 30 & 15 days." },
              ].map((opt) => {
                const active = blockType === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setBlockType(opt.id)}
                    data-testid={`block-type-${opt.id}`}
                    className={`text-left p-3 border transition-colors duration-300 ${
                      active
                        ? "border-[#1A2F24] bg-[#F3F4F1]"
                        : "border-[#DCE0D9] hover:border-[#5C6B61]"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span
                        className={`inline-block h-2 w-5 ${opt.id === "soft" ? "soft-swatch" : ""}`}
                        style={{ background: opt.id === "hard" ? "#1A2F24" : undefined }}
                      />
                      <span className="font-display text-lg text-[#1A2F24]">{opt.label}</span>
                    </span>
                    <span className="block font-body text-[11px] text-[#5C6B61] mt-1">{opt.desc}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-6 border-t border-b border-[#DCE0D9] py-5 grid grid-cols-2 gap-4">
            <div>
              <p className="tracker text-[9px] text-[#5C6B61]">Check-in</p>
              <p className="font-display text-xl text-[#1A2F24] mt-1">
                {fmt(range?.from)}
              </p>
            </div>
            <div>
              <p className="tracker text-[9px] text-[#5C6B61]">Check-out</p>
              <p className="font-display text-xl text-[#1A2F24] mt-1">
                {fmt(range?.to)}
              </p>
            </div>
            <div className="col-span-2">
              <p className="tracker text-[9px] text-[#5C6B61]">Duration</p>
              <p className="font-body text-sm text-[#1A2F24] mt-1">
                {nights(range?.from, range?.to)} night
                {nights(range?.from, range?.to) === 1 ? "" : "s"}
              </p>
            </div>
          </div>

          <div className="mt-6">
            <label
              htmlFor="note"
              className="tracker text-[10px] text-[#5C6B61]"
            >
              A note for the family (optional)
            </label>
            <textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={NOTE_MAX_LENGTH}
              rows={3}
              placeholder="kids&apos; birthday weekend, strawberry season, working remote…"
              data-testid="booking-note"
              className="mt-2 w-full bg-transparent border border-[#DCE0D9] focus:border-[#1A2F24] outline-none p-3 font-body text-sm text-[#1A2F24] resize-none transition-colors duration-300"
            />
            <p className="text-right font-body text-[10px] text-[#5C6B61] mt-1">
              {note.length}/140
            </p>
          </div>

          <DialogFooter className="mt-4 flex gap-3 sm:gap-3">
            <button
              type="button"
              onClick={onClose}
              data-testid="booking-cancel-btn"
              className="px-5 py-3 font-body text-sm tracker text-[#1A2F24] border border-[#1A2F24] hover:bg-[#F3F4F1] transition-colors duration-300"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              data-testid="booking-confirm-btn"
              className="px-6 py-3 font-body text-sm tracker bg-[#1A2F24] text-[#FAF9F6] hover:bg-[#2C4A3A] transition-colors duration-300 disabled:opacity-60"
            >
              {busy ? "Holding…" : soft ? "Pencil it in" : "Hold the house"}
            </button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
